/**
 * Created by Jerker Nord on 2023-03-24.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Loner} from "../../Loner";
import {LonerGameAssets} from "../../LonerGameAssets";
import {LonerGameMode} from "../../server/LonerGameMode";
import {gsap} from "gsap";
import {LonerSound} from "../../sound/LonerSound";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {LonerSpine} from "../../components/LonerSpine";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";

export enum MAIN_FG_ANIMATION_NAMES {
    idle = "idle",
    max_win = "max_win_loop",
    ptw_start = "ptw_start",
    ptw_loop = "ptw_loop",
    ssd_start = "ssd_start",
    ssd_loop = "ssd_loop",
    ttb_start = "ttb_start",
    ttb_loop = "ttb_loop",
    TTB_ARMS_START = "ttb_arms_start",
    TTB_ARMS_PLAYING = "ttb_arms_playing",
    TTB_ARMS_END = "ttb_arms_end",
    transition = "transition",
    PTW_GAME_LOOP = "pin_the_win_loop",
    PTW_CORNER_0_HIT = "corner_win_1",
    PTW_CORNER_1_HIT = "corner_win_2",
    PTW_CORNER_2_HIT = "corner_win_4",
    PTW_CORNER_3_HIT = "corner_win_3",
    PTW_BOUNCE_TOP = "ptw_x_up_top",
    PTW_BOUNCE_BOTTOM = "ptw_x_up_bot",
    PTW_BOUNCE_LEFT = "ptw_x_up_left",
    PTW_BOUNCE_RIGHT = "ptw_x_up_right",
    PTW_BOUNCE_TOP_MINUS = "ptw_x_down_top",
    PTW_BOUNCE_BOTTOM_MINUS = "ptw_x_down_bot",
    PTW_BOUNCE_LEFT_MINUS = "ptw_x_down_left",
    PTW_BOUNCE_RIGHT_MINUS = "ptw_x_down_right",
    PTW_AMBER_GIRLS = "amber_wild_girl",
    BIG_WIN_LEVEL_01 = "bw_1",
    BIG_WIN_LEVEL_02 = "bw_2",
    BIG_WIN_LEVEL_03 = "bw_3",
    BIG_WIN_LEVEL_04 = "bw_4",
    BIG_WIN_LEVEL_05 = "bw_5",
    BIG_WIN_END = "bw_end",
    PIZZA_DROP_1 = "pizza_drops_1",
    PIZZA_DROP_2 = "pizza_drops_2"
}

export class LonerForegroundView extends PIXI.Container implements StageLayer {

    private _foregroundReelsMainSpine: LonerSpine;
    private _foregroundReelsMainContainer: PIXI.Container;
    private _switchForegroundTL: gsap.core.Timeline;
    private _armsTrack: PIXI.spine.core.TrackEntry;
    private _leftBlackCover: PIXI.Sprite;
    private _rightBlackCover: PIXI.Sprite;
    private _topBlackCover: PIXI.Sprite;
    private _bottomBlackCover: PIXI.Sprite;
    private _pizzaDropAnimTL: gsap.core.Timeline | null;
    private _pizzaDropAnimPlaying: boolean = false;

    constructor() {
        super();
        this.name = "LonerForegroundView";
        this.init();
    }

    public playIdlePizzaDrop(pos: number | undefined): void {
        if (!this._pizzaDropAnimTL) {
            this._pizzaDropAnimTL = gsap.timeline();
            this._pizzaDropAnimPlaying = true;
        }
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this._pizzaDropAnimPlaying) {
                    this.playIdlePizzaDrop(undefined);
                }
            }
        });
        tl.add(() => {
            Loner.foregroundView.playAnimationByName(MathHelper.randomNumberInRange(1, 2) === 1 ? MAIN_FG_ANIMATION_NAMES.PIZZA_DROP_1 : MAIN_FG_ANIMATION_NAMES.PIZZA_DROP_2, false, 4);
        }, pos ? pos : MathHelper.randomNumberInRange(15, 30));
        this._pizzaDropAnimTL!.add(tl);
    }

    public stopIdlePizzaDrop(): void {
        this._pizzaDropAnimPlaying = false;
        this._pizzaDropAnimTL!.progress(1);
        this._pizzaDropAnimTL!.kill();
        this._pizzaDropAnimTL = null;
    }

    private init(): void {
        this._leftBlackCover = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._leftBlackCover.tint = 0x000000;
        this._leftBlackCover.width = 1000;
        this._leftBlackCover.height = 1000;
        this._leftBlackCover.position.x = (360 -(1680/2) - this._leftBlackCover.width) + 10;
        this.addChild(this._leftBlackCover);

        this._rightBlackCover = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._rightBlackCover.tint = 0x000000;
        this._rightBlackCover.width = 1000;
        this._rightBlackCover.height = 1000;
        this._rightBlackCover.position.x = (360 + (1680/2)) - 10;
        this.addChild(this._rightBlackCover);

        this._topBlackCover = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._topBlackCover.tint = 0x000000;
        this._topBlackCover.width = 1000;
        this._topBlackCover.height = 1000;
        this._topBlackCover.position.y = (360 -(1680/2) - this._topBlackCover.width) + 10;
        this.addChild(this._topBlackCover);

        this._bottomBlackCover = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._bottomBlackCover.tint = 0x000000;
        this._bottomBlackCover.width = 1000;
        this._bottomBlackCover.height = 1000;
        this._bottomBlackCover.position.y = (360 + (1680/2)) - 30;
        this.addChild(this._bottomBlackCover);

        this._foregroundReelsMainContainer = new PIXI.Container();
        this._foregroundReelsMainSpine = new LonerSpine(Loner.assetLoader.getSpineAsset(LonerGameAssets.MAIN_FG_SPINE));
        this._foregroundReelsMainContainer.addChild(this._foregroundReelsMainSpine);
        this._foregroundReelsMainContainer.position.set(360, 360);
        this.addChild(this._foregroundReelsMainContainer);
    }

    public playTTBArmsAnimation(animationName: string, loop: boolean): void {
        this._armsTrack = this._foregroundReelsMainSpine.state.setAnimation(3, animationName, loop);
    }

    public setTTBArmsTimescale(newTimescale: number): void {
        this._armsTrack.timeScale = newTimescale;
    }

    public playBigWinLevel(level: string): void {
        this._foregroundReelsMainSpine.state.setAnimation(0, level, false);
    }

    public playAnimationByName(animName: MAIN_FG_ANIMATION_NAMES, repeat: boolean = false, track: number = 0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this._foregroundReelsMainSpine.state.setAnimation(track, animName, repeat)
        });
        if (repeat == false) {
            tl.add(gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(animName).duration}));
        }

        return tl;
    }

    public setMode(mode: string): gsap.core.Timeline {
        if (this._switchForegroundTL && this._switchForegroundTL.isActive()) {
            this._switchForegroundTL.pause();
            this._switchForegroundTL.kill();
        }
        this._switchForegroundTL = gsap.timeline();
        if (mode == LonerGameMode.FEATURE_SHOOT_SHOOT_DUCK) {
            this._switchForegroundTL.add(() => {
                this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.ssd_start, false)
            });
            this._switchForegroundTL.add(gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.ssd_start).duration}));

            this._switchForegroundTL.add(() => {
                this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.ssd_loop, true);
            });
        } else if (mode == LonerGameMode.FEATURE_PIN_THE_WIN) {
            this._switchForegroundTL.add(() => {
                this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.ptw_start, false)
            });
            this._switchForegroundTL.add(gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.ptw_start).duration}));
            this._switchForegroundTL.add(() => {
                this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.ptw_loop, true);
            });
        } else if (mode == LonerGameMode.FEATURE_TICK_TICK_BOOM) {
            this._switchForegroundTL.add(() => {
                this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.ttb_start, false)
            });
            this._switchForegroundTL.add(gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.ttb_start).duration}));
        } else if (mode == LonerGameMode.BACK_FROM_PTW) {
            this._switchForegroundTL.add([
                () => {
                    this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.transition, false)
                },
                gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.transition).duration}),
                () => {
                    Loner.soundPlayer.playSound(LonerSound.TRANSITION_STATIC_POST_FEATURE);
                }
            ]);
            this._switchForegroundTL.add(() => {
                this.clearAnimationTrack(0);
            });
        } else if (mode == LonerGameMode.BACK_FROM_SSD) {
            this._switchForegroundTL.add([
                () => {
                    this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.transition, false)
                },
                gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.transition).duration})
            ]);
            this._switchForegroundTL.add(() => {
                this.clearAnimationTrack(0);
            });
        } else if (mode == LonerGameMode.BACK_FROM_TTB) {
            this._switchForegroundTL.add([
                () => {
                    this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.transition, false)
                },
                gsap.timeline({delay: this._foregroundReelsMainSpine.skeleton.data.findAnimation(MAIN_FG_ANIMATION_NAMES.transition).duration}),
                () => {
                    Loner.soundPlayer.playSound(LonerSound.TRANSITION_STATIC_POST_FEATURE);
                }
            ]);
            this._switchForegroundTL.add(() => {
                this.clearAnimationTrack(0);
            });
        } else if (mode == LonerGameMode.MAX_WIN) {
            this._foregroundReelsMainSpine.state.setAnimation(0, MAIN_FG_ANIMATION_NAMES.max_win, true);
        } else if (mode == LonerGameMode.ENDLESS_IDLE) {
            this._foregroundReelsMainSpine.state.setAnimation(1, MAIN_FG_ANIMATION_NAMES.idle, true);
        }

        return this._switchForegroundTL;
    }

    public endForegroundSwitchTL(): void {
        this._switchForegroundTL.totalProgress(1);
    }

    public clearAnimationTrack(track: number): void {
        this._foregroundReelsMainSpine.state.setEmptyAnimation(track, 0);
    }
}